import React, { Fragment, useEffect, useState, useRef } from 'react';
import styles from './viewer.module.css';
import '@mescius/activereportsnet-viewer/dist/jsViewer.min.css';
import '@mescius/activereportsnet-viewer/dist/jsViewer.chart.min.css';

type ViewerProps = {
  onDesign: () => void;
  editable: boolean;
  report: string | undefined;
  undoable?: boolean;
  onUndo: () => void;
  fitToWidth: boolean;
  hideToolbar: boolean;
  hideSidebar: boolean;
  tableAnimation: boolean;
  chartAnimation: boolean;
  theme: any;
};

export default function Viewer({
  onDesign,
  editable,
  report,
  undoable = false,
  onUndo = () => null,
  fitToWidth,
  hideToolbar,
  hideSidebar,
  tableAnimation, chartAnimation, theme
}: ViewerProps) {
  const [css, setCss] = useState<any>('');
  const viewerRef = useRef<any>(null);

  useEffect(() => {
    const options = {
      element: '#viewerContainer',
      animation: {
        hoverChart: {enabled: chartAnimation ?? true},
        loadChart: {enabled: chartAnimation ?? true},
        hoverTable: {enabled: tableAnimation ?? true, backgroundColor: '#ffe5bf'},
      },
      themes: {
        initialTheme: 'activeReports',
        themeSelector: {
          enabled: false,
          availableThemes: ['activeReports']
        }
      },
      initialZoomMode: fitToWidth ? 'FitToWidth' : { percentage: 100 },
      autoBackgroundColor: true,
      availableExports: [ 'Xls', 'Xlsx', 'Doc', 'Docx', 'Pdf', 'Csv', 'Json', 'Xml', 'Tiff'],
      reportItemsStyles: {
        cssUri: 'custom.styles.css'
      },
    };

    if(viewerRef.current != null)
        viewerRef.current.destroy();

    viewerRef.current = GrapeCity.ActiveReports.JSViewer.create(options);

    return () => {
      if(viewerRef.current != null)
        viewerRef.current.destroy();
    }
  }, [fitToWidth, tableAnimation, chartAnimation]);

  useEffect(() => {
    const editBtn = {
      key: '$openDesigner',
      iconCssClass: 'mdi mdi-pencil',
      text: 'Edit in Designer',
      enabled: editable,
      action: () => onDesign(),
    };
    const undoBtn = {
      key: '$undo',
      iconCssClass: 'mdi mdi-undo',
      text: 'Undo',
      title: 'Undo report changes',
      enabled: undoable,
      action: () => onUndo(),
    };
    const update = () => {

      viewerRef.current?.toolbar.desktop.addItem(editBtn);
      viewerRef.current?.toolbar.desktop.addItem(undoBtn);

      viewerRef.current?.toolbar.toggle(!hideToolbar);
      viewerRef.current?.sidebar.toggle(!hideSidebar);

      const layout = [
        '$openDesigner',
        '$navigation',
        '$split',
        '$refresh',
        '$split',
        '$history',
        '$split',
        '$mousemode',
        '$zoom',
        '$split',
        '$print',
        '$singlepagemode',
        '$continuousmode',
        '$galleymode',
      ];
      if (undoable) layout.unshift('$undo');
      viewerRef.current?.toolbar.desktop.layout(layout);
    };

    if (viewerRef.current !== null) {
      viewerRef.current?.toolbar.desktop.removeItem('$openDesigner');
      viewerRef.current?.toolbar.desktop.removeItem('$undo');
      update();
    }
  }, [editable, onDesign]);

  useEffect(() => {
    if (viewerRef.current && report) {
      viewerRef.current.openReport(report);
    }
  }, [report]);

  return (
    <Fragment>
      <style type="text/css" dangerouslySetInnerHTML={{ __html: css }} />
      <div id="viewerContainer" className={styles.root} />
    </Fragment>
  );
}
